import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaSpinner } from "react-icons/fa";
import { Card, CardContent } from "../components/ui/card";
import { Star, ShieldCheck, LogOut } from "lucide-react";
import { useAuthStore } from "../Store/auth";
import { API_BASE, GOOGLE_CLIENT_ID } from "../config/api";
import GoogleSignInButton from "./GoogleSignInButton";

// ───────────────── helpers ─────────────────
// decode Google JWT to get name/email/picture/sub
function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

function pickDate(x) {
  // API may return date | createdAt | updatedAt
  const d = x?.date || x?.createdAt || x?.updatedAt;
  return d ? new Date(d) : new Date(0);
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return "";
  }
}

function clampRating(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return 5;
  return Math.max(1, Math.min(5, v));
}

// ───────────────── Google Sign-In button ─────────────────

function Stars({ value }) {
  const v = clampRating(value);
  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i <= v ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
          }`}
        />
      ))}
    </div>
  );
}

function RatingSummary({ reviews = [] }) {
  const { avg, counts, total } = useMemo(() => {
    if (!reviews.length)
      return { avg: 0, counts: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, total: 0 };
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let sum = 0;
    for (const r of reviews) {
      const rate = clampRating(r.rating);
      counts[rate] += 1;
      sum += rate;
    }
    const total = reviews.length;
    return { avg: sum / total, counts, total };
  }, [reviews]);

  return (
    <Card className="bg-white/70 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Overall rating
            </div>
            <div className="mt-1 flex items-end gap-2">
              <span className="text-3xl font-bold">{avg.toFixed(1)}</span>
              <span className="text-sm text-gray-500">/ 5</span>
            </div>
            <div className="mt-1">
              <Stars value={Math.round(avg)} />
            </div>
            <div className="mt-1 text-xs text-gray-500">
              {total} review{total !== 1 ? "s" : ""}
            </div>
          </div>

          <div className="flex-1 space-y-1">
            {[5, 4, 3, 2, 1].map((n) => {
              const pct = total ? Math.round((counts[n] / total) * 100) : 0;
              return (
                <div key={n} className="flex items-center gap-2">
                  <span className="w-6 text-sm">{n}★</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full bg-yellow-400"
                      style={{ width: `${pct}%` }}
                      aria-label={`${pct}% ${n} star`}
                    />
                  </div>
                  <span className="w-10 text-right text-xs text-gray-500">
                    {counts[n]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ───────────────── Main component ─────────────────
function CoachReviews({ coachId, coachEmail }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Google user (clients)
  const [gUser, setGUser] = useState(null); // { name, email, picture, sub, idToken }
  const [submitting, setSubmitting] = useState(false);

  // form
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  // sort mode: 'newest' | 'highest' | 'lowest'
  const [sortBy, setSortBy] = useState("newest");

  // We still read store in case you need it later for UI, but we don't block coaches anymore
  const { token: coachToken, coachId: loggedCoachId } = useAuthStore();

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE}/Reviews/coach/${coachId}`)
      .then((res) =>
        Array.isArray(res.data) ? res.data : res.data?.$values || []
      )
      .then((data) => {
        // store as-is; we'll handle sorting in UI with sortBy
        setReviews(
          [...data].sort((a, b) => pickDate(b) - pickDate(a)) // default newest
        );
      })
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  }, [coachId]);

  const hasAlreadyReviewed = useMemo(() => {
    if (!gUser?.email) return false;
    return reviews.some(
      (r) =>
        (r.studentEmail?.toLowerCase?.() || "").trim() ===
        gUser.email.toLowerCase().trim()
    );
  }, [gUser, reviews]);

  const reviewDisabledReason = useMemo(() => {
    // Only basic protections now: must be signed in, no duplicate, no empty comment
    if (!gUser) return "Sign in with Google to post a review.";
    if (hasAlreadyReviewed) return "You’ve already reviewed this PT.";
    if (!comment.trim()) return "Write a comment before submitting.";
    return "";
  }, [gUser, hasAlreadyReviewed, comment]);

  // Sorting like Google reviews (newest / highest / lowest)
  const sortedReviews = useMemo(() => {
    const arr = [...reviews];
    if (sortBy === "newest") {
      return arr.sort((a, b) => pickDate(b) - pickDate(a));
    }
    if (sortBy === "highest") {
      return arr.sort((a, b) => {
        const diff = clampRating(b.rating) - clampRating(a.rating);
        if (diff !== 0) return diff;
        // tie-break by date (newer first)
        return pickDate(b) - pickDate(a);
      });
    }
    if (sortBy === "lowest") {
      return arr.sort((a, b) => {
        const diff = clampRating(a.rating) - clampRating(b.rating);
        if (diff !== 0) return diff;
        // tie-break by date (newer first)
        return pickDate(b) - pickDate(a);
      });
    }
    return arr;
  }, [reviews, sortBy]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (reviewDisabledReason) return;

    setSubmitting(true);
    try {
      // Send Google id token so backend can verify authenticity
      const payload = {
        coachId,
        studentName: gUser.name,
        studentEmail: gUser.email,
        rating: clampRating(rating),
        comment: comment.trim(),
        googleIdToken: gUser.idToken,
      };

      await axios.post(`${API_BASE}/Reviews`, payload);

      // Prepend optimistic review so it shows immediately
      const newReview = {
        ...payload,
        date: new Date().toISOString(),
        googleVerified: true,
        avatar: gUser.picture,
      };
      setReviews((prev) => [newReview, ...prev]);
      // Reset
      setComment("");
      setRating(5);
    } catch (err) {
      console.error("Error submitting review:", err);
      const msg =
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string"
          ? err.response.data
          : null) ||
        err?.message ||
        "Something went wrong while submitting your review.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const googleNotConfigured = !GOOGLE_CLIENT_ID;

  return (
    <div className="mt-6 space-y-6">
      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <FaSpinner className="text-3xl text-blue-600 animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary */}
          <RatingSummary reviews={reviews} />

          {/* Header: count + sort */}
          <div className="flex items-center justify-between gap-3 pt-1">
            <div className="text-sm text-gray-600">
              {reviews.length ? (
                <>
                  Showing{" "}
                  <span className="font-medium">{sortedReviews.length}</span>{" "}
                  review{sortedReviews.length !== 1 ? "s" : ""}
                </>
              ) : (
                "No reviews yet."
              )}
            </div>
            {reviews.length > 0 && (
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="text-gray-500">Sort by</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-md border bg-white px-2 py-1 text-xs sm:text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="newest">Newest</option>
                  <option value="highest">Highest rating</option>
                  <option value="lowest">Lowest rating</option>
                </select>
              </div>
            )}
          </div>

          {/* Reviews list */}
          <div className="space-y-4">
            {sortedReviews.length > 0 ? (
              sortedReviews.map((review, index) => (
                <Card key={review.id ?? index} className="bg-white shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        {/* Avatar */}
                        {review.avatar ? (
                          <img
                            src={review.avatar}
                            alt={review.studentName || "avatar"}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="grid h-10 w-10 place-items-center rounded-full bg-gray-200 text-gray-500">
                            {(review.studentName || "U")
                              .slice(0, 1)
                              .toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold leading-tight">
                              {review.studentName || "User"}
                            </p>
                            {review.googleVerified && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                                <ShieldCheck className="h-3.5 w-3.5" />
                                Google Verified
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                            <span>{formatDate(review.date || review.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Stars value={review.rating} />
                        <span className="text-xs text-gray-500">
                          {clampRating(review.rating).toFixed(1)} / 5
                        </span>
                      </div>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">
                      {review.comment}
                    </p>
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-gray-500">No reviews yet.</p>
            )}
          </div>

          {/* Add Review (list first, form after – like Google/YouTube) */}
          <div className="pt-2 border-t border-gray-100">
            <h3 className="mb-3 text-lg font-semibold">Add your review</h3>

            {/* Signed-in header (even if it's a coach email, it's allowed) */}
            {gUser ? (
              <div className="mb-3 flex items-center justify-between rounded-lg border bg-white p-3">
                <div className="flex items-center gap-3">
                  <img
                    src={gUser.picture}
                    alt={gUser.name}
                    className="h-9 w-9 rounded-full object-cover"
                  />
                  <div className="leading-tight">
                    <div className="font-medium">{gUser.name}</div>
                    <div className="text-xs text-gray-500">{gUser.email}</div>
                  </div>
                </div>
                <button
                  onClick={() => setGUser(null)}
                  className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs sm:text-sm hover:bg-gray-50"
                >
                  <LogOut className="h-4 w-4" /> Switch account
                </button>
              </div>
            ) : (
              <div className="mb-4">
                {!googleNotConfigured ? (
                  <>
                    <GoogleSignInButton onCredential={(response) => {
                      const payload = parseJwt(response?.credential || "");
                      if (payload?.email) setGUser({
                        idToken: response.credential,
                        name: payload.name,
                        email: payload.email,
                        picture: payload.picture,
                        sub: payload.sub,
                      });
                    }} />
                    <p className="mt-2 text-xs text-gray-500">
                      Sign in with Google to post a verified review.
                    </p>
                  </>
                ) : (
                  <p className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                    Google Sign-In is not configured yet. Please set
                    VITE_GOOGLE_CLIENT_ID in your environment.
                  </p>
                )}
              </div>
            )}

            {/* Form */}
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">Rating</label>
                <select
                  value={rating}
                  onChange={(e) => setRating(clampRating(e.target.value))}
                  className="w-full rounded border p-2"
                  required
                >
                  {[5, 4, 3, 2, 1].map((num) => (
                    <option key={num} value={num}>
                      {num} Star{num > 1 ? "s" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Comment</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  required
                  className="w-full rounded border p-2"
                  rows="4"
                  placeholder="Share details that help others (e.g., communication, results, punctuality)…"
                />
              </div>

              <button
                type="submit"
                className={`rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 ${
                  reviewDisabledReason || submitting
                    ? "cursor-not-allowed opacity-60"
                    : ""
                }`}
                disabled={!!reviewDisabledReason || submitting}
                title={reviewDisabledReason || ""}
              >
                {submitting ? "Submitting…" : "Submit Review"}
              </button>

              {reviewDisabledReason && (
                <p className="mt-1 text-xs text-gray-500">
                  {reviewDisabledReason}
                </p>
              )}
            </form>
          </div>
        </>
      )}
    </div>
  );
}

export default CoachReviews;
